import { Navigate, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useUser } from '@/lib/auth/useUser';
import { useBetaGate } from '@/lib/auth/useBetaGate';
import { useActiveOrg } from '@/stores/activeOrgStore';
import { Spinner } from '@/components/atoms/Spinner';
import { getConfig } from '@/lib/config';

export const Route = createFileRoute('/')({
  component: IndexPage,
});

function IndexPage() {
  const { isAuthenticated, isLoading, logout, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const me = useUser();
  const gate = useBetaGate();
  const activeOrgId = useActiveOrg((s) => s.activeOrgId);
  const setActiveOrg = useActiveOrg((s) => s.setActiveOrgId);

  // Unauthenticated → Auth0 directly when the legacy page is disabled (F-G1),
  // otherwise the brand-styled in-app /login card.
  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    if (getConfig().features.legacyLoginPageDisabled) {
      loginWithRedirect();
    } else {
      navigate({ to: '/login', replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, loginWithRedirect]);

  // Default active org to the first membership if none selected yet.
  useEffect(() => {
    if (me.data && me.data.memberships.length > 0 && !activeOrgId) {
      setActiveOrg(me.data.memberships[0].organization.id);
    }
  }, [me.data, activeOrgId, setActiveOrg]);

  if (isLoading || me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner aria-label="Loading" />
      </div>
    );
  }

  // Unauthenticated → the effect above is redirecting (to Auth0, or /login when
  // the legacy page is enabled). Show the loading state, NOT the profile-error
  // block below: the `me` query is disabled while signed out, so `!me.data`
  // would otherwise flash "Couldn't load your profile" before the redirect.
  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner aria-label="Redirecting to sign in" />
      </div>
    );
  }

  // Beta gate — /me returned 403 with betaCodeRequired. Route to the gate
  // (checked before the generic error block below, which the 403 would
  // otherwise trip).
  if (gate.isBlocked) {
    return <Navigate to="/signup/beta" replace />;
  }

  // /api/v1/me failed (CORS, 401, network, 5xx, etc.). Surface the failure
  // explicitly instead of spinning forever — silent spinner is what made the
  // CORS failure look like a "blank page" after first deploy.
  if (me.isError || !me.data) {
    const msg = me.error instanceof Error ? me.error.message : 'Unknown error';
    return (
      <div className="mx-auto max-w-xl p-8">
        <h1 className="font-display text-title">Couldn&apos;t load your profile</h1>
        <p className="mt-2 text-body-sm text-muted-foreground">
          The backend rejected the request to <code>/api/v1/me</code>.
        </p>
        <div className="mt-4 rounded-md border border-border bg-card p-4">
          <p className="font-mono text-caption break-words">{msg}</p>
        </div>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            className="rounded-md border border-border bg-primary px-4 py-2 text-primary-foreground"
            onClick={() => me.refetch()}
          >
            Retry
          </button>
          <button
            type="button"
            className="rounded-md border border-border bg-card px-4 py-2 text-foreground"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // The dashboard IS the home for authenticated users (matches the
  // .mockups/developer/dashboard.html composition). No standalone "hub"
  // page — every entry point lives in the AuthenticatedShell sidebar.
  return <Navigate to="/dashboard" replace />;
}

